import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useLocation } from '@tanstack/react-router';
import Avatar from '../ui/Avatar';
import PublicFoldersMenu from './PublicFoldersMenu';
import PublicHighlightsMenu from './PublicHighlightsMenu';
import { useCurrentUser } from '../../services/userHooks';
import { useMobileNavSidebar } from '../../hooks/useUserPreferences';
import type {
  ProfilePublicFolder,
  ProfilePublicHighlightsArticle,
  ProfileUser,
} from '../../types/api';

interface PublicProfileHeaderProps {
  userId: string;
  user?: ProfileUser;
  publicFolders?: ProfilePublicFolder[];
  publicHighlights?: ProfilePublicHighlightsArticle[];
  selectedFolderId?: string;
  selectedHighlightBookmarkId?: string;
  showSeparator?: boolean;
}

/** Shared identity and public-content navigation for every profile page. */
function PublicProfileHeader({
  userId,
  user,
  publicFolders = [],
  publicHighlights = [],
  selectedFolderId,
  selectedHighlightBookmarkId,
  showSeparator = false,
}: PublicProfileHeaderProps) {
  const { pathname } = useLocation();
  const { currentUser } = useCurrentUser();
  const { toggleMobileNavSidebar } = useMobileNavSidebar();
  const displayName = user?.display_name || user?.username;
  const profileUrl = `/p/${user?.username || user?.id || userId}`;
  const avatar = (
    <Avatar
      avatarUrl={user?.profile_picture_url}
      displayName={user?.display_name}
      username={user?.username}
      alt={displayName || t`Profile`}
      className="h-10 w-10 shrink-0"
    />
  );

  return (
    <div className="bg-surface-primary z-header shrink-0">
      <div className="flex items-center px-3 md:px-4 md:py-4">
        {currentUser ? (
          <>
            <button
              type="button"
              className="my-3 shrink-0 md:hidden"
              title={t`Menu`}
              aria-label={t`Menu`}
              onClick={toggleMobileNavSidebar}
            >
              {avatar}
            </button>
            <a
              href={profileUrl}
              className="hidden shrink-0 no-underline md:block"
              aria-label={displayName || t`Profile`}
            >
              {avatar}
            </a>
          </>
        ) : (
          <a
            href={profileUrl}
            className="my-3 shrink-0 no-underline md:my-0"
            aria-label={displayName || t`Profile`}
          >
            {avatar}
          </a>
        )}

        <div className="flex-fluid ml-2 flex min-w-0 flex-col">
          <h1 className="truncate text-sm font-semibold md:text-lg">
            <a href={profileUrl} className="text-copy-primary no-underline">
              {displayName || t`Loading...`}
            </a>
          </h1>
          {(publicFolders.length > 0 || publicHighlights.length > 0) && (
            <div className="flex items-center gap-4">
              <PublicFoldersMenu
                userId={userId}
                folders={publicFolders}
                selectedFolderId={selectedFolderId}
              />
              <PublicHighlightsMenu
                userId={userId}
                articles={publicHighlights}
                selectedBookmarkId={selectedHighlightBookmarkId}
              />
            </div>
          )}
        </div>

        {!currentUser && (
          <a
            href={`/user/login?next=${encodeURIComponent(pathname)}`}
            className="text-copy-secondary shrink-0 px-3 text-sm md:hidden"
          >
            <Trans>Sign In</Trans>
          </a>
        )}
      </div>
      {showSeparator && (
        <div className="bookmark-list-header-divider" role="presentation" />
      )}
    </div>
  );
}

export default PublicProfileHeader;
